import {useEffect} from 'react';
import wavingAndSpeaking from 'assets/images/characters/CarolinaMariadeJesus/waving-speaking.gif';
import {Button} from 'components/ui';
import EmptyWarning from 'components/EmptyWarning';
import {LoadingBox} from 'letrus-ui';
import {
  fetchPracticeTestFeedbackRequest,
  getPracticeTestFeedback,
  getQuestionnaireStatus,
  PracticeTestFeedbackData,
} from 'store/reducers/questionnaires';
import {ApplicationState} from 'store/rootReducer';
import {connect} from 'react-redux';
import {StoreStatus} from 'utils/types/store';
import {useQueryParam} from 'utils/hooks';
import {useNavigate, useParams} from 'react-router-dom';
import resolveSaebFeedbackLevel from 'utils/functions/resolveSaebFeedbackLevel';
import styles from './PracticeTestFeedback.module.scss';
import AnimatedCounterCard from './components/AnimatedCounterCard';
import SaebComparisonSection from './components/SaebComparisonSection';

interface DispatchProps {
  fetchPracticeTestFeedbackRequest: typeof fetchPracticeTestFeedbackRequest;
}

interface StateProps {
  practiceTestFeedback: PracticeTestFeedbackData | null;
  practiceTestFeedbackStatus: StoreStatus;
}

export type PracticeTestFeedbackProps = StateProps & DispatchProps;

function PracticeTestFeedback({
  practiceTestFeedback,
  practiceTestFeedbackStatus,
  fetchPracticeTestFeedbackRequest,
}: PracticeTestFeedbackProps) {
  const navigate = useNavigate();
  const queryParams = useQueryParam();
  const {questionnaire_id} = useParams();
  const learningPathUserInstanceId = queryParams.get('trilha') ?? '';
  const learningPathStepId = queryParams.get('step') ?? '';
  const hasAlreadySeenFeedback = queryParams.get('review') ?? '';

  // Computed ahead of getPracticeTestFeedbackSanitizedData() so resolveSaebFeedbackLevel can
  // know whether this is the final/comparison activity (SAEB 2) and skip the "sua trilha vai te
  // ajudar a avançar" teaser there — the trilha already happened by then.
  const hasComparisonData = practiceTestFeedback?.comparison != null;

  function getPracticeTestFeedbackSanitizedData() {
    if (!practiceTestFeedback || practiceTestFeedback?.score === undefined) {
      return {
        title: '',
        description: '',
        type: 'empty',
        levelIndex: 0,
        source: 'computed' as const,
      };
    }

    const level = resolveSaebFeedbackLevel(
      practiceTestFeedback as PracticeTestFeedbackData,
      !hasComparisonData,
    );

    return {
      title: `${level.title} ${level.emoji}`,
      description: <p>{level.description}</p>,
      type: level.levelIndex === 4 ? 'positive' : 'attention',
      levelIndex: level.levelIndex,
      source: level.source,
    };
  }

  const {title, description, type, levelIndex, source} =
    getPracticeTestFeedbackSanitizedData();

  // "Simplified feedback" is a product concept, not exclusive to the Reading Cycle: the student
  // sees only the level title and description — no Nota/Tempo/Nível/acertos cards. The legacy
  // SAEB simulado (source === 'computed') keeps all cards, so the change does not leak to it.
  // Today the only signal that turns this on is `level_title` from the backend (source ===
  // 'backend', currently only sent for the Reading Cycle) — that's an adapter, not the concept
  // itself, so any future flow (e.g. a "Ciclo Letrus") could opt in the same way without this
  // component knowing about SAEB/reading-cycle naming.
  const isSimplifiedFeedback = source === 'backend';

  // The comparison is shown on the simplified feedback's final questionnaire. It compares the
  // number of correct answers between activity 1 and 3 — real fields confirmed live against
  // CPAL-SAEB (2026-09-18), closes the TODO(fatia3) that used to fall back to a mocked count.
  const comparison = practiceTestFeedback?.comparison;
  const hasComparison = isSimplifiedFeedback && hasComparisonData;

  useEffect(() => {
    if (learningPathUserInstanceId && learningPathStepId) {
      fetchPracticeTestFeedbackRequest(
        learningPathStepId,
        learningPathUserInstanceId,
      );
    }
  }, []);

  // Mari's finding (Slack, 2026-09-01): without this, a fetch failure (404/500 — e.g. accessing
  // by direct URL an activity the student didn't do) left the screen stuck on loading forever.
  // practiceTestFeedbackStatus.error already existed in Redux, it just wasn't checked here.
  // Checks the error BEFORE loading/empty — after a failure, loading turns false but the data
  // never arrives, so without this order it would fall back into the infinite skeleton.
  if (practiceTestFeedbackStatus?.error) {
    return (
      <main className={styles.container}>
        <EmptyWarning subtitle="Atividade não encontrada." />
      </main>
    );
  }

  if (practiceTestFeedbackStatus?.loading || type === 'empty') {
    return (
      <main className={styles.container}>
        <div className={styles.loadingContainer}>
          <LoadingBox width="10rem" height="15rem" borderRadius={25} />
          <LoadingBox width="60rem" height="5rem" borderRadius={25} />
          <div className={styles.loadingCardsContainer}>
            <LoadingBox width="6rem" height="5rem" borderRadius={25} />
            <LoadingBox width="6rem" height="5rem" borderRadius={25} />
            <LoadingBox width="6rem" height="5rem" borderRadius={25} />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.container}>
      <div className={styles.wrapper}>
        <img
          src={wavingAndSpeaking}
          className={styles.image}
          alt="Carolina acenando e falando"
        />
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.description}>{description}</p>

        {/*
          Fatia 5 (Mari's decision, 2026-09-10): in the simplified feedback, NO card is
          shown — Nota, Tempo, Nível AND the correct-answers bar all disappear. Only the text
          (title + description) remains. Activity 2's "little stars" are meant to go here on
          Activities 1 and 3 — waiting on Mari's prototype to define what they measure and how
          they appear (the trail's stars component still needs to be located with the dev). Until
          then, just the text. The legacy SAEB simulado (source: computed) keeps showing
          Nota/Tempo/Nível as today.
        */}
        {!isSimplifiedFeedback && (
          <div className={styles.resultCards}>
            <AnimatedCounterCard
              type={type}
              text="Nota"
              value={practiceTestFeedback?.score ?? 0}
            />
            <AnimatedCounterCard
              type={type}
              text="Tempo Gasto"
              value={practiceTestFeedback?.time_spent ?? 0}
              valueText="min"
            />
            {levelIndex > 0 && (
              <AnimatedCounterCard
                type={type}
                text="Nível"
                value={levelIndex}
              />
            )}
          </div>
        )}

        {hasComparison && comparison && (
          <SaebComparisonSection
            previousCorrect={comparison.previous_correct_answers}
            currentCorrect={comparison.current_correct_answers}
          />
        )}
      </div>
      <div className={styles.buttonsContainer}>
        <Button
          text="Ver Respostas"
          variant="secondary"
          onClick={() => {
            navigate(
              {
                pathname: `/atividades/simulado/${questionnaire_id}`,
                search: `trilha=${learningPathUserInstanceId}&step=${learningPathStepId}`,
              },
              {replace: true},
            );
          }}
        />
        <Button
          text={hasAlreadySeenFeedback ? 'Sair' : 'Concluir'}
          variant="primary"
          backgroundColor={hasAlreadySeenFeedback ? '#FFFFFF' : '#2FAC66'}
          borderColor={hasAlreadySeenFeedback ? '#D9D9D9' : '#306230'}
          textColor={hasAlreadySeenFeedback ? '#000000' : '#FFFFFF'}
          onClick={() => {
            navigate('/');
          }}
        />
      </div>
    </main>
  );
}

export default connect(
  (state: ApplicationState) => ({
    practiceTestFeedback: getPracticeTestFeedback(state),
    practiceTestFeedbackStatus: getQuestionnaireStatus(state),
  }),
  {
    fetchPracticeTestFeedbackRequest,
  },
)(PracticeTestFeedback);
